/**
 * PaywallSheet — "The past was free. That was the proof."
 *
 * Quiet confidence (Jobs rule): no fake urgency, no strikethrough prices.
 * Local tier selection until Stripe lands; Terms + Privacy always linked.
 */
import { LocaleLink as Link } from "../../i18n/LocaleLink";
import { useTranslation } from "react-i18next";
import { X, Sun, Hourglass, MessageCircle } from "lucide-react";
import { useEntitlements } from "../../lib/EntitlementContext";
import { PLANS, getPlan } from "../../lib/subscription";

export default function PaywallSheet() {
  const { t } = useTranslation();
  const {
    paywallOpen,
    closePaywall,
    paywallCopy,
    tier,
    setLocalTier,
    isPremium,
  } = useEntitlements();

  if (!paywallOpen) return null;

  const paid = PLANS.filter((p) => p.id === "pro" || p.id === "scholar");

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-walnut-950/80 backdrop-blur-sm"
        aria-label={t("ui.web.subscription.paywall.close_paywall")}
        onClick={closePaywall}
      />
      <div className="relative z-10 w-full max-w-md mx-auto sm:mx-4 mb-0 sm:mb-0 bg-walnut-900 border border-brass-500/30 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <p className="t-eyebrow">{t("ui.web.subscription.paywall.premium")}</p>
          <button
            type="button"
            onClick={closePaywall}
            className="w-9 h-9 inline-flex items-center justify-center text-parchment-400 hover:text-parchment-100"
            aria-label={t("ui.action.close")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pb-6 space-y-5">
          <header>
            <h2
              id="paywall-title"
              className="font-display italic text-2xl text-parchment-50 leading-snug"
            >
              {t("ui.web.subscription.paywall.title")}
            </h2>
            <p className="font-body text-sm text-parchment-300 mt-2">
              {t("ui.web.subscription.paywall.unlock_copy")}
            </p>
            {paywallCopy && paywallCopy !== "The past was free. That was the proof. Unlock today and everything after." && (
              <p className="font-body text-xs text-brass-300 mt-3 leading-relaxed border-l-2 border-brass-500/40 ps-3">
                {paywallCopy}
              </p>
            )}
          </header>

          <ul className="space-y-2.5 panel p-4">
            <Feature icon={<Sun className="w-4 h-4" />} text={t("ui.web.subscription.paywall.feature_1")} />
            <Feature icon={<Hourglass className="w-4 h-4" />} text={t("ui.web.subscription.paywall.feature_2")} />
            <Feature icon={<MessageCircle className="w-4 h-4" />} text={t("ui.web.subscription.paywall.feature_3")} />
          </ul>

          {isPremium ? (
            <p className="font-body text-sm text-brass-200 text-center">
              {t("ui.web.subscription.paywall.premium_state", {
                tier: t(getPlan(tier).nameKey, { defaultValue: getPlan(tier).name }),
              })}
            </p>
          ) : (
            <div className="space-y-3">
              {paid.map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={async () => {
                    await setLocalTier(plan.id);
                    closePaywall();
                  }}
                  className="w-full text-start panel p-4 hover:border-brass-400/40 transition-colors"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-display italic text-lg text-parchment-50">
                      {t(plan.nameKey, { defaultValue: plan.name })}
                    </span>
                    <span className="font-mono text-sm text-brass-200">
                      ${plan.price}
                      <span className="text-parchment-400 text-micro">{t("ui.web.subscription.per_month_short")}</span>
                    </span>
                  </div>
                  <p className="font-body text-xs text-parchment-400 mt-1">
                    {t(plan.descriptionKey, { defaultValue: plan.description })}
                  </p>
                  {tier === plan.id && (
                    <p className="font-mono text-micro text-brass-300 mt-2">{t("ui.web.subscription.paywall.current_preference")}</p>
                  )}
                </button>
              ))}
              <p className="font-mono text-micro text-parchment-400 text-center leading-relaxed">
                {t("ui.web.subscription.paywall.preview_copy")}
              </p>
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-4 pt-1 font-mono text-micro text-parchment-400">
            <Link to="/privacy" onClick={closePaywall} className="hover:text-brass-300">
              {t("ui.web.subscription.paywall.privacy")}
            </Link>
            <Link to="/terms" onClick={closePaywall} className="hover:text-brass-300">
              {t("ui.web.subscription.paywall.terms")}
            </Link>
            <Link to="/plans" onClick={closePaywall} className="hover:text-brass-300">
              {t("ui.web.subscription.paywall.all_plans")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <li className="flex items-center gap-3 text-parchment-100">
      <span className="text-brass-300 shrink-0">{icon}</span>
      <span className="font-body text-sm">{text}</span>
    </li>
  );
}
